/**
 * How the two creation forms issue a document: save it as a draft, then
 * promote the draft, so that the number is assigned by the server at the
 * moment of issue and a refusal leaves the operator's work saved.
 *
 * Pure: no Svelte and no browser API, so node:test exercises every branch.
 *
 * The invariant everything below serves: a new draft is created only when the
 * form holds no draft id, or when the id it holds is **proven absent** — the
 * company-scoped GET answered 404. A DELETE or PATCH answering 404 proves
 * nothing, because both answer so for a draft that has meanwhile been
 * promoted; and an answer that may hide a committed promotion (no status, or a
 * 5xx) never leads to a new draft. The draft id is the issued invoice's id, so
 * reconciling is always a GET of the same id.
 *
 * `state` is the form's memory across attempts:
 * - `draftId`: the draft this form saved;
 * - `savedPayload`: the snapshot of the payload that draft holds;
 * - `promotedWith`: the snapshot of the last promotion body *sent* for it.
 *
 * That memory is lost on a reload, which is why every draft saved here is
 * marked `origin: 'form'`: the server then checks it for a lookalike issued in
 * the last 24 hours before promoting it, and answers `possible_duplicate` for
 * the operator to confirm or cancel. The confirmation is the warning's
 * `duplicateCheckToken`, valid only for the form exactly as it was warned
 * about: see acknowledgementFor().
 */

export class IssueOutcomeUnknownError extends Error {
  constructor(draftId, state, cause) {
    super('issue-via-draft: could not learn whether the document was issued', cause ? { cause } : undefined);
    this.name = 'IssueOutcomeUnknownError';
    this.code = 'issue_outcome_unknown';
    this.draftId = draftId;
    this.state = state;
  }
}

export function emptyIssueState() {
  return { draftId: null, savedPayload: null, promotedWith: null };
}

/** A key-order independent snapshot, so equal payloads always compare equal. */
export function snapshotOf(value) {
  return JSON.stringify(value, (_key, v) => (v && typeof v === 'object' && !Array.isArray(v)
    ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, v[k]]))
    : v));
}

// No HTTP status (network error, timeout) or a 5xx: a proxy error or a lost
// acknowledgement may be hiding a committed promotion. A 4xx is definitive —
// the gateway refuses only after its transaction rolled back.
const isAmbiguous = (err) => !err?.status || err.status >= 500;

// A PATCH or DELETE telling that the row is no longer the draft it read: a 404,
// or the 409 the gateway answers when the row changed while the request waited
// for its lock (a promotion committing is exactly that). Neither proves the
// draft is gone, so both lead to a probe.
const DRAFT_MOVED_CODES = new Set(['not_a_draft', 'stale_revision', 'not_found']);
const rowMoved = (err) => err?.status === 404 || (err?.status === 409 && DRAFT_MOVED_CODES.has(err.code));

/**
 * The form as one click of «Emetti» sends it: the draft payload and the
 * promotion body, without any acknowledgement.
 */
export function issueKeyOf(payload, promoteBody) {
  const { acknowledgeDuplicates: _ack, ...body } = promoteBody || {};
  return snapshotOf({ payload, body });
}

/**
 * The token «Emetti comunque» may send: the warning's own, and only while the
 * form is still the one it was given for (`warning.formKey`, an issueKeyOf()).
 * After any edit it is null, so the edited draft is saved and checked afresh.
 */
export function acknowledgementFor(warning, payload, promoteBody) {
  if (!warning?.token) return null;
  return warning.formKey === issueKeyOf(payload, promoteBody) ? warning.token : null;
}

/**
 * What the company-scoped GET says about a draft id:
 * 'draft' | 'issued' | 'absent' | 'unknown'.
 */
export async function probeDraft(api, companyId, id) {
  try {
    const invoice = await api.getInvoice(companyId, id);
    return invoice?.status === 'draft' ? 'draft' : 'issued';
  } catch (err) {
    return err?.status === 404 ? 'absent' : 'unknown';
  }
}

/**
 * Save and issue one document.
 *
 * Resolves `{ outcome: 'issued', result, invoiceId, state }`,
 * `{ outcome: 'already_issued', invoiceId, changesDiscarded, state }` or
 * `{ outcome: 'possible_duplicate', matches, windowHours, duplicateCheckToken, draftId, state }` —
 * the last keeps the draft, and the same call with
 * `promoteBody.acknowledgeDuplicates = duplicateCheckToken` issues it.
 * Throws the original error, with `draftId` and `state` set, on a refusal, or
 * an `IssueOutcomeUnknownError` when the outcome could not be learned.
 */
export async function issueViaDraft({ api, companyId, state, payload, promoteBody, immutable = false }) {
  let current = { ...emptyIssueState(), ...(state || {}) };
  // The attempt that may already have issued, as it stood before this one
  // wrote anything. Every already_issued exit compares against it: the issued
  // document is that one, so any difference means the latest edits were not
  // issued. Forgotten once this attempt works on a draft of its own.
  let earlier = current.draftId ? { ...current } : null;
  const payloadKey = snapshotOf(payload);
  const sent = { ...payload, origin: 'form' };
  const bodyKey = snapshotOf(promoteBody);

  const changesDiscarded = () => earlier !== null && (
    payloadKey !== earlier.savedPayload
    || (earlier.promotedWith !== null && bodyKey !== earlier.promotedWith));
  const alreadyIssued = (invoiceId) => ({
    outcome: 'already_issued', invoiceId, changesDiscarded: changesDiscarded(), state: emptyIssueState(),
  });
  const refused = (err, kept) => {
    if (err && typeof err === 'object') {
      err.draftId = kept.draftId;
      err.state = kept;
    }
    return err;
  };
  const unknown = (cause) => new IssueOutcomeUnknownError(current.draftId, current, cause);

  async function createFresh() {
    current = emptyIssueState();
    earlier = null;
    let created;
    try {
      created = await api.createDraft(companyId, sent);
    } catch (err) {
      throw refused(err, current);
    }
    current = { draftId: created.id, savedPayload: payloadKey, promotedWith: null };
  }

  // 1. Reconcile a kept draft before writing anything: a promotion still in
  // flight from the previous click is found here.
  if (current.draftId) {
    const found = await probeDraft(api, companyId, current.draftId);
    if (found === 'issued') return alreadyIssued(current.draftId);
    if (found === 'unknown') throw unknown();
    if (found === 'absent') { current = emptyIssueState(); earlier = null; }
  }

  // 2. Save the draft.
  if (!current.draftId) {
    await createFresh();
  } else if (payloadKey !== current.savedPayload) {
    if (immutable) {
      // A self-invoice draft cannot be edited: delete it and create it again.
      try {
        await api.deleteDraft(companyId, current.draftId);
      } catch (err) {
        if (!rowMoved(err) && !isAmbiguous(err)) throw refused(err, current);
        const found = await probeDraft(api, companyId, current.draftId);
        if (found === 'issued') return alreadyIssued(current.draftId);
        if (found === 'unknown') throw unknown(err);
        if (found === 'draft') throw refused(err, current);
      }
      await createFresh();
    } else {
      for (let attempt = 0; ; attempt += 1) {
        try {
          await api.updateDraft(companyId, current.draftId, sent);
          current = { ...current, savedPayload: payloadKey };
          break;
        } catch (err) {
          if (!rowMoved(err) && !isAmbiguous(err)) throw refused(err, current);
          const found = await probeDraft(api, companyId, current.draftId);
          if (found === 'issued') return alreadyIssued(current.draftId);
          if (found === 'unknown') throw unknown(err);
          if (found === 'absent') { await createFresh(); break; }
          if (attempt >= 1) throw refused(err, current);
        }
      }
    }
  }

  // 3. Promote the draft held now.
  current = { ...current, promotedWith: bodyKey };
  const { draftId } = current;
  try {
    const result = await api.promoteDraft(companyId, draftId, promoteBody);
    return { outcome: 'issued', result, invoiceId: result?.id ?? draftId, state: emptyIssueState() };
  } catch (err) {
    // 4. Read the answer.
    if (err?.status === 409 && err.code === 'draft_no_longer_promotable') return alreadyIssued(draftId);
    if (err?.status === 409 && err.code === 'possible_duplicate') {
      // Refused before anything was written, so this promotion issued nothing
      // and is not remembered as one that might have.
      return {
        outcome: 'possible_duplicate',
        matches: err.matches || [],
        windowHours: err.windowHours ?? null,
        duplicateCheckToken: err.duplicateCheckToken ?? null,
        draftId,
        state: { ...current, promotedWith: null },
      };
    }
    if (!isAmbiguous(err)) throw refused(err, current);
    const found = await probeDraft(api, companyId, draftId);
    if (found === 'issued') return alreadyIssued(draftId);
    if (found === 'draft') throw refused(err, current);
    if (found === 'absent') throw refused(err, emptyIssueState());
    throw unknown(err);
  }
}
